// @ts-nocheck
/**
 * Device push token registration for the native RecruitLook Events app.
 *
 * Everything here is a no-op in a browser: every entry point is guarded by
 * Capacitor.isNativePlatform(), so the website never sees a permission prompt.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { getSessionUser } from '@/lib/authSession';

// Exactly the string send-push-notification expects in device_tokens.platform.
export const PUSH_PLATFORM = 'ios';

let listenersAttached = false;
let lastToken: string | null = null;

// This device's token, persisted so sign-out can always narrow its delete to
// this phone's row — even after a relaunch, when lastToken is null again.
const TOKEN_STORAGE_KEY = 'rle_push_device_token';

function getStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStoredToken(token: string | null) {
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {}
}

/* ------------------------------------------------------------------ */
/* Diagnostics — a small observable snapshot the Profile screen shows. */
/* ------------------------------------------------------------------ */

export interface PushDiagnostics {
  native: boolean;
  session: 'unknown' | 'present' | 'missing';
  permission: 'unknown' | 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';
  registerCalled: boolean;
  tokenReceived: boolean;
  tokenPreview: string | null;
  writeStatus: 'idle' | 'saving' | 'ok' | 'error';
  writeError: string | null;
  registrationError: string | null;
  lastStep: string;
}

const diagnostics: PushDiagnostics = {
  native: false,
  session: 'unknown',
  permission: 'unknown',
  registerCalled: false,
  tokenReceived: false,
  tokenPreview: null,
  writeStatus: 'idle',
  writeError: null,
  registrationError: null,
  lastStep: 'not started',
};

type Listener = (d: PushDiagnostics) => void;
const subscribers = new Set<Listener>();

function emit(patch: Partial<PushDiagnostics>) {
  Object.assign(diagnostics, patch);
  const snapshot = { ...diagnostics };
  subscribers.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {}
  });
}

export function getPushDiagnostics(): PushDiagnostics {
  return { ...diagnostics };
}

export function subscribePushDiagnostics(fn: Listener): () => void {
  subscribers.add(fn);
  fn({ ...diagnostics });
  return () => subscribers.delete(fn);
}

function isNative(): boolean {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

async function saveToken(token: string) {
  lastToken = token;
  emit({
    tokenReceived: true,
    tokenPreview: token.slice(0, 8),
    writeStatus: 'saving',
    writeError: null,
    lastStep: 'token received, writing row',
  });

  const { data: { user } } = await getSessionUser();
  if (!user) {
    emit({
      session: 'missing',
      writeStatus: 'error',
      writeError: 'no signed-in session when the token arrived',
      lastStep: 'token received but nothing written',
    });
    console.warn('[push] token received with no session; not stored');
    return;
  }

  // Unique constraint is (user_id, token) — upsert so relaunches never duplicate.
  const { error } = await supabase
    .from('device_tokens')
    .upsert(
      { user_id: user.id, token, platform: PUSH_PLATFORM },
      { onConflict: 'user_id,token' }
    );

  if (error) {
    const detail = [error.code, error.message, error.details, error.hint]
      .filter(Boolean)
      .join(' — ');
    emit({
      session: 'present',
      writeStatus: 'error',
      writeError: detail || 'unknown write error',
      lastStep: 'write to device_tokens failed',
    });
    console.error('[push] failed to store device token:', detail, error);
  } else {
    emit({
      session: 'present',
      writeStatus: 'ok',
      writeError: null,
      lastStep: 'token stored',
    });
    console.log('[push] device token stored');
  }
}

async function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  // Listener first: the very first token can arrive immediately after register().
  await PushNotifications.addListener('registration', (t) => {
    // Keep the token on hand immediately — even if the write below is skipped
    // (no session yet), sign-out must know which row belongs to this device.
    lastToken = t.value;
    setStoredToken(t.value);
    saveToken(t.value).catch((e) => {
      emit({
        writeStatus: 'error',
        writeError: String(e?.message || e),
        lastStep: 'write threw',
      });
      console.error('[push] saveToken threw:', e);
    });
  });

  await PushNotifications.addListener('registrationError', (err) => {
    const msg = typeof err === 'string' ? err : (err?.error || JSON.stringify(err));
    emit({ registrationError: msg, lastStep: 'Apple refused registration' });
    console.error('[push] registrationError:', msg);
  });
}

/**
 * Ask for permission (once, iOS only allows one prompt) and register with APNs.
 * Safe to call on every launch/resume — Apple reissues tokens.
 */
export async function registerPushNotifications(
  opts: { prompt?: boolean } = {}
): Promise<void> {
  const native = isNative();
  emit({ native, lastStep: 'starting' });
  if (!native) {
    emit({ lastStep: 'not running as a native app' });
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      emit({ session: 'missing', lastStep: 'no session yet, nothing registered' });
      return; // device_tokens requires user_id = auth.uid()
    }
    emit({ session: 'present' });

    const { PushNotifications } = await import('@capacitor/push-notifications');
    await attachListeners();

    let perm = await PushNotifications.checkPermissions();
    emit({ permission: perm.receive, lastStep: 'permission checked' });

    const needsPrompt =
      perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale';
    if (needsPrompt) {
      // Only ask on the screen the user lands on after signing in — never at launch.
      if (!opts.prompt) {
        emit({ lastStep: 'waiting to ask permission' });
        return;
      }
      perm = await PushNotifications.requestPermissions();
      emit({ permission: perm.receive, lastStep: 'permission requested' });
    }
    if (perm.receive !== 'granted') {
      emit({ lastStep: 'permission not granted' });
      console.log('[push] permission not granted:', perm.receive);
      return; // denial writes nothing and throws nothing
    }

    await PushNotifications.register();
    emit({ registerCalled: true, lastStep: 'registered with Apple, awaiting token' });
  } catch (err: any) {
    emit({
      registrationError: String(err?.message || err),
      lastStep: 'registration threw',
    });
    console.error('[push] registerPushNotifications failed:', err);
  }
}

/**
 * Called before sign-out: a token belongs to a person, not a phone.
 */
export async function unregisterPushNotifications(userId?: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { data: { user } } = await getSessionUser();
    const uid = userId ?? user?.id;
    if (!uid) return;

    // Only ever delete THIS device's row. If we don't know this phone's
    // token, delete nothing — the user's other devices must keep their rows.
    const token = lastToken ?? getStoredToken();
    if (!token) {
      console.warn('[push] sign-out: no token known for this device; leaving device_tokens untouched');
      emit({ lastStep: 'sign-out: no device token on hand, nothing removed' });
    } else {
    const { error } = await supabase
      .from('device_tokens')
      .delete()
      .eq('user_id', uid)
      .eq('token', token);
    if (error) {
      emit({ writeStatus: 'error', writeError: `${error.code || ''} ${error.message}`.trim() });
      console.error('[push] failed to remove device token:', error.message);
    } else {
      emit({
        writeStatus: 'idle',
        writeError: null,
        tokenReceived: false,
        tokenPreview: null,
        registerCalled: false,
        lastStep: 'token removed',
      });
      setStoredToken(null);
    }
    }

    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      await PushNotifications.removeAllListeners();
      listenersAttached = false;
    } catch {}
    lastToken = null;
  } catch (err) {
    emit({ writeError: String(err?.message || err), lastStep: 'removal threw' });
    console.error('[push] unregisterPushNotifications failed:', err);
  }
}
